import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack, Typography } from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import { useAiChatContext } from "src/_modules/AiSupportChat/context/AiChatProvider";
import SuggestionChips from "src/_modules/AiSupportChat/components/SuggestionChips";
import ConversationList from "src/_modules/AiSupportChat/components/ConversationList";
import SupportFooter from "src/_modules/AiSupportChat/components/SupportFooter";
import { RETENTION_FALLBACK_DAYS } from "src/_modules/AiSupportChat/constants";

/**
 * The panel's home view (plan §3.5), top to bottom: greeting, disclosure,
 * "Start new conversation", suggested questions, previous conversations;
 * the WhatsApp footer stays under the scrolling part.
 *
 * "Start new" only opens a draft: the backend creates the conversation on
 * the first message, so nothing is requested here. While an answer streams
 * (`busy`), starting another conversation is disabled (C17).
 *
 * @param {object} props
 * @param {() => void} props.onStartNew
 * @param {(text: string) => void} props.onSuggestion
 * @param {(id: string) => void} props.onOpenConversation
 * @param {boolean} [props.busy]
 * @param {string | null} [props.liveConversationId] stays openable while busy
 */
export default function HomeView({
  onStartNew,
  onSuggestion,
  onOpenConversation,
  busy = false,
  liveConversationId = null,
}) {
  const { t } = useTranslation("aiSupport");
  const { eligibility } = useAiChatContext();
  const profileName = useSelector(
    (state) => state.entities.profile.profile?.name
  );

  const name = typeof profileName === "string" ? profileName.trim() : "";
  const retentionDays =
    eligibility.data?.retentionDays ?? RETENTION_FALLBACK_DAYS;

  return (
    <Box
      sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
    >
      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <Stack spacing={2.5} sx={{ p: 2 }}>
          <Box>
            <Typography component="p" variant="h6" sx={{ fontWeight: 600 }}>
              {name ? t("home.greeting", { name }) : t("home.greetingNoName")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t("home.subtitle")}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
            <InfoOutlined
              fontSize="small"
              color="action"
              aria-hidden="true"
              sx={{ mt: 0.25 }}
            />
            <Typography variant="caption" color="text.secondary">
              {t("disclosure", { days: retentionDays })}
            </Typography>
          </Box>

          {/* `aria-disabled`, not `disabled`, while busy: Back in the middle
              of an answer lands here, and a disabled button can't take the
              autoFocus (focus would fall to <body>, out of Esc's reach) */}
          <Button
            variant="contained"
            fullWidth
            autoFocus
            aria-disabled={busy}
            className={busy ? "Mui-disabled" : undefined}
            onClick={busy ? undefined : onStartNew}
            sx={{ textTransform: "none" }}
          >
            {t("panel.newConversation")}
          </Button>

          <SuggestionChips onSelect={onSuggestion} disabled={busy} />

          <ConversationList
            retentionDays={retentionDays}
            onOpen={onOpenConversation}
            busy={busy}
            liveConversationId={liveConversationId}
          />
        </Stack>
      </Box>
      <SupportFooter whatsappUrl={eligibility.data?.support?.whatsappUrl} />
    </Box>
  );
}
